import Styles from './Images.module.scss'
import { useImagePickerContext } from 'components/ImagePickerModal/ImagePickerContext'
import { Button } from 'blaze-ui/Button'
import { Images } from 'blaze-ui/icons'
import { Text } from 'blaze-ui/Text/Text'
import { useEffect, useMemo } from 'react'
import { useUnsplashImages } from 'hooks/queries/useUnsplashImages'
import { collectResults } from 'utils/query'
import { ShimmeringColor } from 'components/BrandWizardVisualAnalysisModal/LoadingState'
import { getVideoMimeType } from 'components/Design/UploadsTab/components/StockVideosGallery'
import { sample } from 'lodash'
import { MediaFilePreview } from './MediaFilePreview'
import { useFlipperFlag } from 'hooks/useFlipperFlag'
import { useStockVideos, VIDEO_CATEGORIES } from 'hooks/queries/designer/useStockVideos'

interface Props {
  isDisabled?: boolean
  imageKeywords?: string[]
  isLoading?: boolean
}

export const StockImagesAndVideos = ({ isDisabled, imageKeywords, isLoading }: Props) => {
  const imagePickerContext = useImagePickerContext()
  const isDesktopWizardsUpdatesOn = useFlipperFlag('desktop_wizards_updates')
  const { selectedImages, initialQuery, setProvider, toggleSelectedImage } = imagePickerContext
  const query = imageKeywords?.join(' ') || initialQuery || ''
  const unsplashQuery = useUnsplashImages({ query, perPage: 4 })
  const results = collectResults(unsplashQuery, (data) => data.results || data)
  const stockImages = results.map((item) => {
    return {
      url: item.urls.regular,
      unsplash: {
        authorUsername: item.user.username,
        authorName: item.user.name,
        id: item.id,
        query,
      },
    }
  })

  const category = useMemo(() => sample(VIDEO_CATEGORIES), [])
  const stockVideosQuery = useStockVideos({
    query,
    pageSize: 24,
    videoType: 'all',
    category: !query ? category : undefined,
  })

  const videos = stockVideosQuery.data?.pages.flatMap((page) => page.videos) || []

  const videoFiles = videos.map((video) => ({
    id: video.id,
    url: video.videos.medium.url,
    source_url: video.videos.medium.url,
    thumbnail_url: video.videos.medium.thumbnail,
    duration: video.duration,
    variants: [],
    mime_type: video.videos.medium.mime_type ?? getVideoMimeType(video.videos.medium.url),
    title: video.id,
    preview_variant_url: video.videos.tiny.url,
  }))

  const isStockLoading = unsplashQuery.isLoading || stockVideosQuery.isLoading

  useEffect(() => {
    if (stockImages.length > 0 && !selectedImages.length) toggleSelectedImage(stockImages[0])
  }, [unsplashQuery.isFetched]) // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className={Styles.imagesSection}>
      <div className={Styles.header}>
        <Text variant="largeList">Choose a stock photo or video</Text>
      </div>
      <div className={Styles.imagesCard}>
        <div className={Styles.imagesGrid}>
          {(isLoading || isStockLoading) &&
            Array.from({ length: 4 }).map((_, i) => (
              <ShimmeringColor
                aspectRatio={1}
                width={isDesktopWizardsUpdatesOn ? 152 : 141}
                height={isDesktopWizardsUpdatesOn ? 152 : 141}
                key={i}
              />
            ))}
          {!isLoading &&
            !isStockLoading &&
            Array.from({ length: 4 }).map((_, i) => {
              if (i === 0 || i === 3 || !videoFiles[0] || !videoFiles[1]) {
                if (!stockImages[i])
                  return (
                    <ShimmeringColor
                      aspectRatio={1}
                      width={isDesktopWizardsUpdatesOn ? 152 : 141}
                      height={isDesktopWizardsUpdatesOn ? 152 : 141}
                      key={i}
                    />
                  )

                return (
                  <MediaFilePreview
                    ariaLabel="unsplash-image"
                    key={stockImages[i].unsplash.id}
                    mediaFile={stockImages[i]}
                    isDisabled={isDisabled}
                    isSelected={stockImages[i].unsplash.id === selectedImages[0]?.unsplash?.id}
                  />
                )
              }

              return (
                <MediaFilePreview
                  ariaLabel="pixabay-video"
                  key={videoFiles[i % 2].id}
                  mediaFile={videoFiles[i % 2]}
                  showVideoPreview={false}
                  isDisabled={isDisabled}
                  isSelected={videoFiles[i % 2].id === selectedImages[0]?.id}
                />
              )
            })}
        </div>
        <Button
          frontIcon={Images}
          variant="tertiary"
          isDisabled={stockImages.length === 0 || isDisabled}
          onPress={() => setProvider('stock')}
        >
          Browse all stock photos & videos
        </Button>
      </div>
    </div>
  )
}
